import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { createXRStore, XR } from "@react-three/xr";
import { api } from "@/lib/api";
import { useApp } from "@/lib/store";
import { VRProvider, useVR } from "./useVR";
import Scene from "./Scene";
import { HUD, MapModal, SettingsPanel, Joystick } from "./ui/Overlays";
import InspectPanel from "./ui/InspectPanel";
import ScientistChat from "./ui/ScientistChat";
import COAPanel from "./ui/COAPanel";
import VRCart from "./ui/VRCart";
import { MolecularPanel, TimelinePanel } from "./ui/ResearchPanels";
import ImmersiveLite from "./ImmersiveLite";
import { Headset, MousePointer2, ShoppingBag, Sparkles, Download, X, Smartphone } from "lucide-react";

const hasWebGL = () => {
  try { const c = document.createElement("canvas"); return !!(c.getContext("webgl2") || c.getContext("webgl")); }
  catch { return false; }
};
const isMobile = () => /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || window.innerWidth < 768;
const dprFor = (q) => ({ auto: [1, 1.75], high: [1, 2], medium: [1, 1.25], low: [1, 1] }[q] || [1, 1.5]);

function CanvasHost({ xrStore }) {
  const vrctx = useVR();
  const navigate = useNavigate();
  const vr = {
    ...vrctx,
    onCalc: () => navigate("/tools/calculator"),
    onCheckout: () => navigate("/checkout"),
  };
  return (
    <Canvas shadows dpr={dprFor(vrctx.quality)} camera={{ fov: 62, position: [0, 1.6, 13], near: 0.1, far: 80 }} gl={{ antialias: vrctx.quality !== "low", powerPreference: "high-performance" }}>
      <XR store={xrStore}>
        <Scene vr={vr} />
      </XR>
    </Canvas>
  );
}

function Experience() {
  const vr = useVR();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();
  const [phase, setPhase] = useState("entry"); // entry | immersive
  const [favs, setFavs] = useState(new Set());
  const [sciCtx, setSciCtx] = useState({});
  const xrStore = useMemo(() => createXRStore(), []);
  const [progress, setProgress] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showInstallHelp, setShowInstallHelp] = useState(false);

  useEffect(() => {
    const bip = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const done = () => { setInstalled(true); setInstallPrompt(null); };
    const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
    if (standalone) setInstalled(true);
    window.addEventListener("beforeinstallprompt", bip);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("beforeinstallprompt", bip); window.removeEventListener("appinstalled", done); };
  }, []);

  const installApp = async () => {
    vr.track("vr_install_click");
    if (installPrompt) { installPrompt.prompt(); const r = await installPrompt.userChoice.catch(() => null); if (r?.outcome === "accepted") setInstalled(true); setInstallPrompt(null); }
    else setShowInstallHelp(true);
  };

  // fake asset-prep progress on the loading screen
  useEffect(() => {
    if (phase !== "entry") return;
    let p = 0; const id = setInterval(() => { p = Math.min(100, p + Math.random() * 22); setProgress(Math.round(p)); if (p >= 100) clearInterval(id); }, 180);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => { if (user) api.get("/vr/favorites").then(({ data }) => setFavs(new Set(data))).catch(() => {}); }, [user]);

  // deep links
  useEffect(() => {
    const parts = location.pathname.split("/").filter(Boolean); // ["vr", ...]
    if (parts.length >= 2) {
      const seg = parts[1];
      if (seg === "product" && parts[2]) { setPhase("immersive"); vr.setRoom("gallery"); setTimeout(() => vr.setInspect(parts[2]), 400); }
      else if (seg === "research") { setPhase("immersive"); vr.setRoom("research"); if (parts[2]) setTimeout(() => vr.setScientist("nova"), 300); }
      else if (seg === "quality") { setPhase("immersive"); vr.setRoom("quality"); setTimeout(() => vr.setShowCOA(true), 300); }
      else if (seg === "scientist") { setPhase("immersive"); vr.setRoom("scientists"); if (parts[2]) setTimeout(() => vr.setScientist(parts[2]), 300); }
    }
  }, []); // eslint-disable-line

  const toggleFav = async (slug) => {
    try { const { data } = await api.post(`/vr/favorite/${slug}`); setFavs((s) => { const n = new Set(s); data.favorited ? n.add(slug) : n.delete(slug); return n; }); }
    catch { navigate("/auth"); }
  };

  const enterImmersive = () => { setPhase("immersive"); vr.track("enter_immersive"); };
  const enterVR = () => { setPhase("immersive"); vr.track("enter_vr_headset", { device: "vr" }); setTimeout(() => xrStore.enterVR().catch(() => {}), 300); };

  if (phase === "entry") {
    return (
      <div className="fixed inset-0 z-[60] bg-[#05070a] flex items-center justify-center overflow-hidden" data-testid="vr-entry">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 40%, rgba(56,225,255,0.18), transparent 60%)" }} />
        <div className="relative text-center px-6">
          <div className="mx-auto w-16 h-16 rounded-2xl border border-cyan-400/40 flex items-center justify-center mb-6" style={{ boxShadow: "0 0 40px rgba(56,225,255,0.3)" }}>
            <Sparkles className="text-cyan-300" />
          </div>
          <div className="text-xs font-mono tracking-[0.4em] text-cyan-300">CG LABZ VR LABORATORY</div>
          <h1 className="text-5xl sm:text-6xl font-semibold text-white mt-4" style={{ textShadow: "0 0 30px rgba(56,225,255,0.25)" }}>ENTER THE LAB</h1>
          <p className="text-slate-400 mt-3 font-mono text-xs tracking-widest">EXPLORE SCIENCE. DISCOVER PEPTIDES. ENTER A HIGHER YOU.</p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={enterVR} data-testid="vr-enter-vr" className="px-6 py-3.5 rounded-xl bg-cyan-400 text-black font-semibold flex items-center justify-center gap-2 hover:bg-cyan-300 transition"><Headset size={18} /> Enter VR</button>
            <button onClick={enterImmersive} data-testid="vr-enter-immersive" className="px-6 py-3.5 rounded-xl border border-cyan-400/40 text-cyan-100 font-semibold flex items-center justify-center gap-2 hover:bg-cyan-500/10 transition"><MousePointer2 size={18} /> Enter Immersive Mode</button>
            <button onClick={() => navigate("/shop")} data-testid="vr-quick-shop" className="px-6 py-3.5 rounded-xl border border-white/15 text-slate-200 font-semibold flex items-center justify-center gap-2 hover:bg-white/5 transition"><ShoppingBag size={18} /> Quick Shop</button>
          </div>

          <div className="mt-5 flex justify-center">
            {installed ? (
              <span className="text-xs font-mono tracking-widest text-emerald-300/80 flex items-center gap-2" data-testid="vr-installed"><Download size={14} /> APP INSTALLED · LAUNCH FROM YOUR HOME SCREEN OR HEADSET</span>
            ) : (
              <button onClick={installApp} data-testid="vr-install-btn"
                className="group px-5 py-2.5 rounded-full border border-cyan-400/40 bg-cyan-500/5 text-cyan-100 text-sm font-semibold flex items-center gap-2 hover:bg-cyan-500/15 transition">
                <Download size={16} /> Install / Add to Headset
                <span className="text-[10px] font-mono text-cyan-300/60 ml-1 hidden sm:inline">QUEST · DESKTOP · MOBILE</span>
              </button>
            )}
          </div>

          <div className="mt-10 w-64 mx-auto">
            <div className="h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-cyan-400 transition-all" style={{ width: `${progress}%` }} /></div>
            <div className="text-[10px] font-mono text-slate-500 mt-2 tracking-widest">PREPARING LABORATORY · {progress}%</div>
          </div>
        </div>
        {showInstallHelp && <InstallHelp onClose={() => setShowInstallHelp(false)} />}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] bg-[#05070a]" data-testid="vr-immersive">
      <CanvasHost xrStore={xrStore} />
      <HUD vr={vr} onExit={() => navigate("/")} />
      <Joystick input={vr.input} />
      {vr.showMap && <MapModal vr={vr} />}
      {vr.showSettings && <SettingsPanel vr={vr} />}
      {vr.inspect && <InspectPanel slug={vr.inspect} room={vr.room} track={vr.track} onClose={() => vr.setInspect(null)} onOpenCart={() => vr.setShowCart(true)} onAsk={(pep) => { setSciCtx({ peptide_slug: pep }); vr.setInspect(null); vr.setScientist("nova"); }} onFav={toggleFav} faved={favs.has(vr.inspect)} />}
      {vr.showCart && <VRCart onClose={() => vr.setShowCart(false)} track={vr.track} />}
      {vr.showMolecule && <MolecularPanel onClose={() => vr.setShowMolecule(false)} onAsk={(pep) => { setSciCtx({ peptide_slug: pep }); vr.setShowMolecule(false); vr.setScientist("nova"); }} />}
      {vr.showTimeline && <TimelinePanel onClose={() => vr.setShowTimeline(false)} />}
      {vr.scientist && <ScientistChat world={vr.world} startKey={vr.scientist} context={vr.inspect ? { product_slug: vr.inspect } : sciCtx} onClose={() => { vr.setScientist(null); setSciCtx({}); }} />}
      {vr.showCOA && <COAPanel onClose={() => vr.setShowCOA(false)} track={vr.track} />}
    </div>
  );
}

export default function VRApp() {
  const device = isMobile() ? "mobile" : "desktop";
  if (!hasWebGL()) {
    return (
      <VRProvider device="lite">
        <LiteWrap />
      </VRProvider>
    );
  }
  return (
    <VRProvider device={device}>
      <Experience />
    </VRProvider>
  );
}

function LiteWrap() {
  const vr = useVR();
  return <ImmersiveLite world={vr.world} track={vr.track} />;
}

function InstallHelp({ onClose }) {
  const Step = ({ n, children }) => (
    <li className="flex gap-3"><span className="shrink-0 w-6 h-6 rounded-full bg-cyan-400 text-black text-xs font-bold flex items-center justify-center">{n}</span><span className="text-sm text-slate-300">{children}</span></li>
  );
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="vr-install-help">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-3xl bg-[#080c12] border border-cyan-400/25 p-6">
        <button onClick={onClose} className="absolute top-3 right-3 p-2 text-slate-400 hover:text-white"><X size={18} /></button>
        <div className="flex items-center gap-2 text-cyan-300 font-mono text-xs tracking-widest"><Download size={14} /> INSTALL CG LABZ VR</div>
        <h2 className="text-2xl font-semibold text-white mt-1">Get it as an app</h2>
        <p className="text-sm text-slate-400 mt-1">Install once, then launch straight into the lab — no browser needed.</p>

        <div className="mt-5 rounded-2xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-white font-semibold mb-2"><Headset size={16} className="text-cyan-300" /> Meta Quest</div>
          <ol className="space-y-2">
            <Step n={1}>Open the <b>Quest Browser</b> and go to this page (<span className="font-mono text-cyan-300">/vr</span>).</Step>
            <Step n={2}>Tap the <b>⋮ menu</b> in the browser toolbar.</Step>
            <Step n={3}>Choose <b>Install</b> / <b>Add to Home</b> — the CG Labz icon appears in your app library.</Step>
            <Step n={4}>Launch it, then tap <b>Enter VR</b>.</Step>
          </ol>
        </div>

        <div className="mt-3 rounded-2xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-white font-semibold mb-2"><Smartphone size={16} className="text-cyan-300" /> Phone & Desktop</div>
          <ol className="space-y-2">
            <Step n={1}><b>Chrome/Edge:</b> use the <b>Install</b> icon in the address bar (or ⋮ → Install).</Step>
            <Step n={2}><b>iPhone/iPad (Safari):</b> <b>Share</b> → <b>Add to Home Screen</b>.</Step>
          </ol>
        </div>
        <button onClick={onClose} className="mt-5 w-full rounded-xl bg-cyan-400 text-black font-semibold py-3">Got it</button>
      </div>
    </div>
  );
}
